/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Button, Card, Collapse, Tooltip } from '@blueprintjs/core';
import { ColorAliases } from 'styles/variables';
import { Sizes } from 'styles/variables-slykit';
import { hoverRevealChildrenDisplay, interactiveListItem, overflowEllipsis } from 'styles/styledComponentUtils';
import { FacetLabel } from 'components/facets/facetItem';
import { FACET_STATE } from 'hooks/useFacetsState';
import { FacetCheckbox } from 'components/facets/facetCheckbox';

export const MetaDataSideBar = ({ selectedDefinitionName, facets, timeline, cssStyles, description }) => {
	const { activeFacets, activeHierarchicalFacets } = facets || {};
	const { timeProperty, timeExtents } = timeline || {};
	const [collapseStates, setCollapseStates] = useState({});

	useEffect(() => {
		const newStates = {};
		Object.keys(activeFacets || []).forEach(facet => {
			newStates[facet] = true;
		});
		Object.keys(activeHierarchicalFacets || []).forEach(facet => {
			newStates[facet] = true
		});
		setCollapseStates(newStates);
	}, [activeFacets, activeHierarchicalFacets]);

	const toggleCollapse = (propertyName) => {
		setCollapseStates(state => ({
			...state,
			[propertyName]: !state[propertyName]
		}))
	}

	const formatDate = (date) => moment(date).utc().format('HH:mm:ss MM/DD/YYYY');

	return (
		<Card css={[wrapper, cssStyles]} elevation={1}>
			<div css={container}>
				{/* Data */}
				<Content label={'Source'} content={selectedDefinitionName} />
				{/* Description */}
				{description && 
					<Content label={'Description'} content={description} />
				}
				{/* Timeframe */}
				{timeProperty &&
					<div>
						<Content label={'Timeframe Property'} content={timeProperty?.name} />
						<Content label={'Date Range'} content={
							timeExtents ? (<div>
								{`${formatDate(timeExtents[0])} - ${formatDate(timeExtents[0])}`}
							</div>) : 'None'
						} />
					</div>
				}
				{/* Filters */}
				<div>
					<div css={labelStyle}>Filters</div>
					{activeFacets && Object.entries(activeFacets).length > 0 ? Object.entries(activeFacets).map(facet => {
						const propertyName = facet[0];
						const entries = facet[1];
						const isOpen = collapseStates[propertyName];

						return (
							<div css={facetGroup} key={propertyName}>
								<div 
									css={[propertyHeader, hoverRevealChildrenDisplay, interactiveListItem]}
									onClick={() => toggleCollapse(propertyName)}
								>
									<Tooltip
										hoverOpenDelay={2000}
										content={isOpen ? 'Collapse' : 'Open'}
									>
										<Button
											rightIcon={isOpen ? 'chevron-down' : 'chevron-right'}
											minimal
											small
											css={css`margin-left:-4px;`}
										/>
									</Tooltip>
									<div css={title}>{propertyName}</div>
								</div>
								<Collapse isOpen={isOpen}>
									<div css={css`display: flex; flex-direction: column; padding-bottom: 5px;`}>
										{Object.keys(entries).map((entry) => {
											const isExcluded = entries[entry] === FACET_STATE.OFF;
											return (
												<div css={css`display: flex;`}>
													<FacetCheckbox
														isSelected={!isExcluded}
														isExcluded={isExcluded}
														disabled
													/>
													<FacetLabel
														name={entry}
														key={entry}
														isExcluded={isExcluded}
													/>
												</div>
											)
										})}
										{/* TODO: handle hierarchical facets as well */}
									</div>
								</Collapse>
							</div>
						)
					}) : 
					<Content content={'None'} />	
				}
				</div>
			</div>
		</Card>
	)
}


const Content = ({ label, content }) => {
	return (
		<div>
			<div css={labelStyle}>
				{label}
			</div>
			<div css={contentStyle}>
				{content}
			</div>
		</div>
	)
}

const labelStyle = css`
	${overflowEllipsis}
	flex: 1 1 auto;
	font-weight: bold;
	font-size: ${Sizes.md};
`;

const contentStyle = css`
	flex: 1 1 auto;
	margin: 10px 20px;
`;

const wrapper = css`
	box-shadow: rgba(0,0,0,0.1) 0 12px 48px 0;
	overflow: auto;

	.bp3-input {
		box-shadow: none;
		font-weight: bold;
	}
`;

const container = css`
	width: 300px;
	.bp3-form-content {
    flex: 1;
  }
`;

const facetGroup = css`
	border-bottom: 1px solid ${ColorAliases.PT_DIVIDER_BLACK};
`;

const propertyHeader = css`
	width: 100%;
	padding: ${Sizes.sm} 0;
	display: flex;
	align-items: center;
`;

const title = css`
	${overflowEllipsis}
	flex: 1 1 auto;
	margin: 0 ${Sizes.xxsm};
	font-weight: bold;
`;